import type { VexFlowGraphicalNote } from "opensheetmusicdisplay";
import type { Doc } from '../../convex/_generated/dataModel';

// Button component types
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonColor = 'primary' | 'secondary' | 'surface' | 'error';
export type ButtonVariant = 'flat' | 'outlined' | 'text';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonRounded = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

// Icon component types
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconColor = 'current' | 'primary' | 'secondary' | 'white' | 'error' | 'success' | 'warning';

export type TimeSignature = {
  numerator: number;
  denominator: number;
};

export type SlotState = {
  tempo: number;
  timeSignature: TimeSignature;
  isPlaying: boolean;
};

export type SoundType = 
  | 'triangle' 
  | 'square' 
  | 'sine' 
  | 'sawtooth' 
  | 'woodblock' 
  | 'pluck'
  | 'clave'
  | 'hardwood'
  | 'ping'
  | 'logic'
  | 'ableton'
  | 'cubase'
  | 'fl-studio'
  | 'maschine'
  | 'mpc'
  | 'protools default'
  | 'protools marimba'
  | 'reason'
  | 'sonar';

export enum HitStatus {
  Unplayed = 'unplayed',
  Missed = 'missed',
  Good = 'good', // Hit with > 50ms timing offset  
  Perfect = 'perfect' // Hit with <= 50ms timing offset
}

// A stripped <direction>/<words> element's text plus whatever styling it declared
// in the source MusicXML, so the label redrawn after render (useSheetMusic's
// drawNoteLabel) matches what OSMD would have rendered instead of a size we
// invent from the notehead.
export type NoteLabel = {
  text: string;
  fontSize?: string;
  fontFamily?: string;
};

export type Note = {
  measureIndex: number;
  timestamp: number;
  played: boolean;
  graphicalNote: VexFlowGraphicalNote | unknown;
  noteName: string;
  possibleMidiNumbers: number[];
  // x-coordinate with respsect to the sheet music container. Required (unlike y)
  // because scroll/timing math reads it unconditionally from construction
  // onward — it starts at a placeholder 0 ("will be calculated after the sheet
  // music is rendered", see useSheetMusic's processNotes) and is overwritten
  // with the real value by computeOsmdNoteCoordinates, but nothing needs to
  // tell those two states apart.
  x: number;
  // Top of the notehead, same local coordinate space as x. Used for
  // annotations. Stays optional, and must not default to 0 like x: unlike x,
  // undefined here is a meaningful third state ("not positioned yet") that
  // findNearestNote (useNoteAnnotations.ts) and the multi-row grouping in
  // GameAreaSheetMusic.vue explicitly filter on — collapsing it to 0 would
  // make an un-positioned note indistinguishable from one genuinely at the
  // top of the sheet.
  y?: number;
  hitStatus?: HitStatus;
  timingOffset?: number; // The timing offset in ms (positive = late, negative = early)
  // A repeat struck out of a tremolo. Shares the parent's graphicalNote, since the
  // page draws the whole roll as that one notehead.
  isTremoloRepeat?: boolean;
  // A grace note (see computeGraceOffsetsMs). Still its own hittable/judged note
  // with its own timestamp, but excluded from the target-line/scroll motion
  // curve (computeSheetScrollPosition, getTrailingNoteSpeed, computeMsPerPixel)
  // so the sheet doesn't jerk toward it on the way to its main note.
  isGrace?: boolean;
  decoration?: 'ghost' | 'rimshot';
  // Arbitrary text drawn above the note (e.g. drum sticking "R"/"L") — see
  // distributeNoteLabelDirections/drawNoteLabel in useSheetMusic.ts for why
  // this is drawn ourselves after render instead of left as a MusicXML
  // <direction>/<words> element.
  label?: NoteLabel;
};

// ─── Piece sections ───────────────────────────────────────────────────────────
export type MusicalPieces = Doc<'musicalPieces'>;
export type PieceSections = MusicalPieces['sections'];
export type PieceSection = NonNullable<PieceSections>[number];
export type UserLoopSection = {
  startMeasureIndex: number;
  endMeasureIndex: number;
};

// ─── Musical piece field types ────────────────────────────────────────────────
export type MusicalPieceDifficulty = MusicalPieces['difficultyId'];
export type MusicalPieceDifficultyStars = MusicalPieces['difficultyStars'];
export type MusicalPieceCategory = MusicalPieces['category'];
export type MusicalPieceLesson = MusicalPieces['lessonId'];
export type MusicalPieceTempo = MusicalPieces['tempo'];
export type MusicalPieceTimeSignatures = MusicalPieces['timeSignatures'];
export type MusicalPieceLanguage = MusicalPieces['language'];
export type MusicalPieceGenre = MusicalPieces['genre'];
export type MusicalPieceAssignedUsers = NonNullable<MusicalPieces['assignedUsers']>;

// ─── OSMD measure bounds (for sections/loop overlays) ────────────────────────

/** Options for computing measure bounds from an OSMD instance */
export type MeasureBoundsOptions = {
  /** The OSMD instance */
  osmd?: unknown; // OpenSheetMusicDisplay
  /** OSMD target element (contains the SVG) — also the coordinate frame bounds are reported in */
  osmdEl?: HTMLElement;
  /** Whether to include vertical (y, height) data. Default: true */
  includeVertical?: boolean;
  /** Whether to compute row grouping for multi-line sections. Default: false */
  includeRowGrouping?: boolean;
  /** Extra vertical padding for section overlays. Default: 0 */
  sectionPaddingY?: number;
};

/** Computed bounds for a single measure */
export type MeasureBound = {
  measureIndex: number;
  x: number;
  width: number;
  y?: number;
  height?: number;
  rowGroup?: number;
};

export type SectionSegment = {
  x: number;
  y: number;
  width: number;
  height: number;
  isFirst: boolean;
  isLast: boolean;
};
